"use client";

import { useRouter } from "next/navigation";
import { Star } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UnifiedFollowButton } from "@/components/UnifiedFollowButton";
import { ProfileStatInline } from "@/components/Profile/profile-stat-inline";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { cn } from "@/lib/utils";

export type SellerRowData = {
  id: string;
  username: string;
  avatar_url: string | null;
  followers_count: number;
  is_following: boolean;
  sales_count?: number;
  rating?: number | null;
  rating_count?: number;
};

/**
 * SellerRow — a compact "seller to discover" list row: avatar + handle + a stat
 * line (★ rating with review count, then the follower count as an icon+number
 * via ProfileStatInline) + the canonical Follow button. The whole row is a
 * hoverable link to the profile. Rail-fit (320px) and reused wherever a
 * seller-suggestion list is shown.
 */
export function SellerRow({
  seller,
  currentUserId,
  className,
}: {
  seller: SellerRowData;
  currentUserId?: string | null;
  className?: string;
}) {
  const router = useRouter();

  return (
    <div
      className={cn(
        "-mx-2 flex items-center gap-2 rounded-xl p-2 transition-colors hover:bg-accent",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => router.push(`/${seller.username}`)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <Avatar className="size-10 shrink-0">
          <AvatarImage src={getAvatarUrl(seller.avatar_url) || undefined} alt={seller.username} />
          <AvatarFallback>{seller.username?.[0]?.toUpperCase() ?? "U"}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-sm font-semibold leading-tight">
            <span className="truncate">@{seller.username}</span>
          </div>
          <div className="mt-1 flex items-center gap-2.5 text-[12px]">
            {seller.rating != null ? (
              <span
                className="inline-flex shrink-0 items-center gap-1"
                title={`${seller.rating} rating${seller.rating_count ? ` (${seller.rating_count} reviews)` : ""}`}
              >
                <Star className="size-3.5 fill-amber-400 text-amber-400" />
                <span className="font-bold tabular-nums text-foreground">{seller.rating}</span>
                {seller.rating_count ? (
                  <span className="tabular-nums text-muted-foreground">({seller.rating_count})</span>
                ) : null}
              </span>
            ) : null}
            <ProfileStatInline
              stat="followers"
              value={seller.followers_count}
              iconClassName="size-3.5 text-muted-foreground"
              valueClassName="text-[12px] font-bold text-foreground"
            />
          </div>
        </div>
      </button>
      <UnifiedFollowButton
        currentUserId={currentUserId ?? null}
        profileId={seller.id}
        initialFollowing={seller.is_following}
        profileUsername={seller.username}
        profileAvatarUrl={seller.avatar_url}
        variant="compact"
        context="carousel"
        foil
        className="shrink-0"
      />
    </div>
  );
}
